import { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { getConsent, setConsent } from '../analytics';
import { useDestrierTheme } from '../ThemeContext';

export default function CookieConsent() {
  const { c } = useDestrierTheme();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (getConsent()) return;
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    setConsent('accepted');
    setShow(false);
  };

  const handleDecline = () => {
    setConsent('declined');
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.35)',
              zIndex: 9998,
            }}
          />

          {/* Modal — true center using flex */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: '2vh',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 260 }}
              style={{ width: '90%', maxWidth: 440, pointerEvents: 'auto' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 2,
                  p: { xs: 3.5, md: 4 },
                  borderRadius: '20px',
                  backgroundColor: c.mode === 'dark' ? '#252530' : '#fff',
                  border: `1px solid ${c.mode === 'dark' ? '#ffffff10' : '#00000008'}`,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
                }}
              >
                {/* Logo */}
                <img
                  src={c.mode === 'dark' ? '/logo-light.svg' : '/logo.svg'}
                  alt="Destrier"
                  style={{ width: 48, height: 48 }}
                />

                <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  We use cookies to understand how you use Destrier and improve your experience.
                </Typography>

                {/* Accept */}
                <Button
                  onClick={handleAccept}
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.3,
                    borderRadius: '100px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    backgroundColor: c.accent,
                    color: '#fff',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: c.accent, filter: 'brightness(0.9)', boxShadow: 'none' },
                  }}
                >
                  Accept
                </Button>

                {/* Decline */}
                <Typography
                  onClick={handleDecline}
                  sx={{
                    color: c.textMuted,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    '&:hover': { color: c.textDark },
                    transition: 'color 0.2s',
                  }}
                >
                  Reject
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
}
